import { memo } from "react";
import { useI18n } from "@/i18n";
import type { Translations } from "@/i18n";
import { colors, spacing, typography, transitions } from "@/design-system/tokens";

const HERO_IMAGE =
  "https://assets-nexo.s3.us-east-1.amazonaws.com/nexo-warehouse/Logistics-pana.svg";

export const Hero = memo(function Hero() {
  const { t } = useI18n();

  return (
    <section
      aria-label="Hero"
      style={{
        background: "#fff",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: `120px ${spacing.xl} ${spacing["3xl"]}`,
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          alignItems: "center",
          gap: spacing["2xl"],
        }}
        className="hero-grid"
      >
        <HeroContent t={t} />
        <HeroImage />
      </div>
    </section>
  );
});

const HeroContent = memo(function HeroContent({ t }: { t: Translations }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing.xl }}>
      <TagBadge t={t} />

      <div>
        <h1
          style={{
            fontFamily: typography.fontFamily,
            fontWeight: typography.weight.bold,
            lineHeight: 0.92,
            margin: 0,
            letterSpacing: "-0.04em",
          }}
        >
          <span
            style={{
              display: "block",
              fontSize: "clamp(4rem, 9vw, 7.5rem)",
              color: "#111",
            }}
          >
            {t.hero.titleTop}
          </span>
          <span
            style={{
              display: "block",
              fontSize: "clamp(2.4rem, 5.5vw, 4.2rem)",
              color: colors.primary,
              lineHeight: 1.15,
              marginTop: spacing.sm,
            }}
          >
            {t.hero.titleBottom}
          </span>
        </h1>
        <p
          style={{
            fontFamily: typography.fontFamily,
            fontSize: typography.size.base,
            color: "#666",
            lineHeight: 1.75,
            maxWidth: "400px",
            marginTop: spacing.lg,
          }}
        >
          {t.hero.subtitle}
        </p>
      </div>

      <a
        href="#pricing"
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.sm,
          fontWeight: typography.weight.bold,
          padding: "12px 28px",
          borderRadius: "9999px",
          textDecoration: "none",
          display: "inline-block",
          background: colors.primary,
          color: "#fff",
          border: "none",
          transition: `all ${transitions.fast}`,
          width: "fit-content",
        }}
        onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = colors.primaryHover; }}
        onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.background = colors.primary; }}
      >
        {t.pricing.cta}
      </a>
    </div>
  );
});

const TagBadge = memo(function TagBadge({ t }: { t: Translations }) {
  const tags = [t.hero.tagSecure, t.hero.tagScalable, t.hero.tagRealTime];

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: spacing.sm,
        width: "fit-content",
      }}
    >
      <div
        style={{
          width: "28px",
          height: "28px",
          background: "#111",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {tags.map((tag, i) => (
          <span
            key={tag}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            {i > 0 && (
              <span
                aria-hidden="true"
                style={{
                  color: "#ccc",
                  fontSize: typography.size.sm,
                  lineHeight: 1,
                }}
              >
                /
              </span>
            )}
            <span
              style={{
                fontFamily: typography.fontFamily,
                fontSize: typography.size.sm,
                fontWeight: typography.weight.semibold,
                color: "#111",
              }}
            >
              {tag}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
});

const HeroCTA = memo(function HeroCTA({
  label,
  primary = false,
}: {
  label: string;
  primary?: boolean;
}) {
  return (
    <a
      href={primary ? "#contact" : "#resources"}
      style={{
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        fontWeight: typography.weight.bold,
        padding: `${spacing.md} ${spacing.xl}`,
        borderRadius: "9999px",
        textDecoration: "none",
        transition: `all ${transitions.fast}`,
        display: "inline-block",
        background: primary ? colors.primary : "transparent",
        color: primary ? "#fff" : "#111",
        border: primary ? `2px solid ${colors.primary}` : "2px solid #ddd",
        letterSpacing: "0.01em",
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.background = primary ? colors.primaryHover : "#f5f5f5";
        el.style.borderColor = primary ? colors.primaryHover : "#bbb";
        el.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.background = primary ? colors.primary : "transparent";
        el.style.borderColor = primary ? colors.primary : "#ddd";
        el.style.transform = "translateY(0)";
      }}
    >
      {label}
    </a>
  );
});

const HeroImage = memo(function HeroImage() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "fadeInUp 0.7s ease both",
      }}
    >
      <img
        src={HERO_IMAGE}
        alt="Logistics and warehouse management illustration"
        style={{
          width: "100%",
          maxWidth: "560px",
          height: "auto",
          objectFit: "contain",
        }}
        loading="eager"
      />
    </div>
  );
});
